import InterfaceGroupSelector from 'app/components/interfaceGroup/InterfaceGroupSelector';
import { Box, Button, Checkbox as CoreCheckbox, Flex, Heading, Link, Switch, Text } from 'core/components';
import Table from 'core/components/HTMLTable';
import { Checkbox, Field, Form, FormDialog, InputGroup, RadioGroup } from 'core/form';
import Duration from 'core/form/components/Duration';
import { runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { opacify } from 'polished';
import React, { Component } from 'react';
import {
  DEFAULT_GNMI_INTERVAL,
  fastMeasurements,
  MINIMUM_GNMI_INTERVAL,
  MINIMUM_SNMP_INTERVAL
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import styled from 'styled-components';
import {
  getMeasurementSubscriptionState,
  groupMeasurements,
  MEASUREMENT_SUBSCRIPTION_FIELDS,
  serializeMeasurementSettings,
  shouldDisableUnlistedMeasurement
} from './monitoringTemplateConstants';
import { createMonitoringTemplateFields, createPollingSnmpDefaults } from './monitoringTemplateSharedConfig';
import PollingConfigSection from './PollingConfigSection';

const pollingSnmpFieldConfigs = createPollingSnmpDefaults();

const fields = createMonitoringTemplateFields({
  runOnlyListedReportsField: {
    // used programmatically
    // not as an ui input
    defaultValue: false
  },
  userEditablePollingSnmpFieldConfigs: pollingSnmpFieldConfigs
});

/**
settings schema
{
    "flow-mode": "standard" | "minimum", // minimum includes only metadata, standard also cpu, mem, interface stats
    "run-only-listed-reports": boolean, // if true, only reports that are explicitly listed in the template will be run
    "intervals": { // default intervals, can be overridden in measurements
        "fastMetrics": "1m",
        "slowMetrics": "5m",
        "metadata": "4h"
    },
    "measurements": { // all measurements with all metrics are enabled with above intervals by default
        "/system/cpus": {
            "interval": "5m",
            "st_interval": "1m",
            "enabled": true,
            "metrics": {
                "total/instant": { "enabled": true }
                "ssCpuRawIdle": { "enabled": false }
            }
        }
    },
    "polling": { // Optional
      "snmp": {
        "retries": 10,
        "timeout": "5s",
        "max-oids": 10,
        "max-repetitions": 10,
        "workers": 10
      }
    }
}
*/

// necessary for form registration/reporting in form store
// without it there is a warning thrown
const options = { name: 'Monitoring Profile' };

const HR = styled.hr`
  margin-top: 16px;
  margin-bottom: 16px;
  border-color: ${({ theme }) => opacify(0.18, theme.borderColors.thin)};
  border-style: solid;
  border-width: 1px 0 0;
`;

const MeasurementTable = styled(Table)`
  tbody > tr > td:first-child {
    padding: 0;
  }

  thead th {
    border-bottom: 1px solid ${({ theme }) => theme.borderColors.tabs};
  }

  tbody::before {
    content: '';
    display: table-row;
    height: 8px;
  }

  && tbody > tr:first-child > th,
  && tbody > tr:first-child > td {
    box-shadow: none;
  }
`;

function measurementPath(measurement) {
  if (measurement.includes('.')) {
    // use square brackets to handle measurements that have dots in their name
    // TODO this notation doesn't work in validatorjs, so these fields don't get validated
    return `settings.measurements['${measurement}']`;
  }
  return `settings.measurements.${measurement}`;
}

@Form({ fields, options })
@inject('$metrics', '$auth')
@observer
export default class ModernMonitoringTemplateForm extends Component {
  state = {
    interfaceSelectorOpen: false,
    expandedMeasurementGroups: {
      essentials: true,
      customSnmp: true,
      other: true
    }
  };

  get uniqueNameRule() {
    const { model } = this.props;
    return `not_in:${model.collection.models.filter((m) => m !== model).map((m) => m.get('name'))}`;
  }

  get isFlowTemplate() {
    const { model, templateType } = this.props;
    return model.get('template_type') === 'FLOW' || templateType === 'FLOW';
  }

  get allowedMeasurementsForFlow() {
    return ['/system/cpus', '/system/memory', '/interfaces/counters'];
  }

  get isReadOnly() {
    const { readOnly, model } = this.props;
    return readOnly || model.isPreset;
  }

  get measurements() {
    const { $metrics } = this.props;
    const allMeasurements = $metrics.availableMetricsCollection.models.filter((model) => {
      const measurement = model.get('measurement') || '';
      return !measurement.startsWith('/kentik/') && !measurement.startsWith('kentik/');
    });

    // For FLOW templates, only allow specific measurements
    if (this.isFlowTemplate) {
      return allMeasurements.filter((model) => this.allowedMeasurementsForFlow.includes(model.get('measurement')));
    }

    return allMeasurements;
  }

  get subscribedMeasurementGroups() {
    const { form } = this.props;
    return {
      essentials: form.getValue(MEASUREMENT_SUBSCRIPTION_FIELDS.essentials),
      customSnmp: form.getValue(MEASUREMENT_SUBSCRIPTION_FIELDS.customSnmp)
    };
  }

  get allMeasurementsSubscribed() {
    const { form } = this.props;
    return form.getValue(MEASUREMENT_SUBSCRIPTION_FIELDS.everything);
  }

  constructor(props) {
    super(props);
    const { form, model, templateType } = this.props;
    const { measurements } = this;
    const settings = model.get('settings');

    // Set template_type if creating new template
    if (model.isNew && templateType) {
      model.set('template_type', templateType);
    }
    // For FLOW templates, set default intervals to 5 minutes metrics, 3h for metadata
    const { isFlowTemplate } = this;
    let fastInterval;
    let slowInterval;

    if (isFlowTemplate) {
      const defaultInterval = '5m';
      const defaultMetadataInterval = '3h';
      form.setValue('template_type', 'FLOW');
      form.setValue('settings.intervals.fastMetrics', defaultInterval);
      form.setValue('settings.intervals.slowMetrics', defaultInterval);
      form.setValue('settings.intervals.metadata', defaultMetadataInterval);
      fastInterval = defaultInterval;
      slowInterval = defaultInterval;
    } else {
      fastInterval = form.getValue('settings.intervals.fastMetrics');
      slowInterval = form.getValue('settings.intervals.slowMetrics');
    }

    measurements.forEach((m) => {
      const name = m.get('measurement');
      const metrics = m.get('storage').Metrics || {};
      const isFast = fastMeasurements.includes(name);
      const defaultInterval = isFast ? fastInterval : slowInterval;

      const measurementFields = {
        [`${measurementPath(name)}.enabled`]: { label: name, defaultValue: true },
        [`${measurementPath(name)}.interval`]: {
          rules: ['validDuration', `minDuration:${MINIMUM_SNMP_INTERVAL}`],
          defaultValue: defaultInterval
        },
        [`${measurementPath(name)}.st_interval`]: {
          rules: ['validDuration', `minDuration:${MINIMUM_GNMI_INTERVAL}`],
          defaultValue: DEFAULT_GNMI_INTERVAL
        }
      };

      Object.entries(metrics).forEach(([metric, data]) => {
        measurementFields[`${measurementPath(name)}.metrics.${metric}.enabled`] = {
          label: data.Label || metric,
          defaultValue: true
        };
      });

      form.addFields(measurementFields);
    });

    // re-set model to init new fields
    form.setModel(model);

    // When run-only-listed-reports is true, measurements absent from settings.measurements
    // were intentionally excluded — treat them as disabled rather than defaulting to enabled.
    // helps checkboxes match what was actually saved for existing templates.
    const runOnlyListed = settings['run-only-listed-reports'] === true;
    const subscriptionState = getMeasurementSubscriptionState({
      settings,
      defaultToEssentials: model.isNew && !this.isFlowTemplate
    });
    Object.entries(MEASUREMENT_SUBSCRIPTION_FIELDS).forEach(([subscriptionKey, fieldName]) => {
      form.getField(fieldName)?.init(subscriptionState[subscriptionKey]);
    });

    const subscribedNames = new Set(
      groupMeasurements(measurements)
        .filter((group) => group.tag && subscriptionState[group.key])
        .flatMap((group) => group.measurements.map((measurement) => measurement.get('measurement')))
    );

    measurements.forEach((measurement) => {
      const name = measurement.get('measurement');
      if (
        shouldDisableUnlistedMeasurement({
          name,
          settings,
          runOnlyListedReports: runOnlyListed,
          subscribedNames,
          everythingSubscribed: subscriptionState.everything
        })
      ) {
        form.getField(`${measurementPath(name)}.enabled`)?.init(false);
      }
    });

    this.syncSimpleMeasurementSelectionsFromEnabledFields();
  }

  syncSimpleMeasurementSelectionsFromEnabledFields = () => {
    const { form } = this.props;
    const { measurements } = this;

    form.getMatchingFields('simple.measurements').forEach((simpleField) => {
      const values = simpleField.options
        .map((option) => option.value)
        .filter((optionValue) => {
          if (optionValue.endsWith('*')) {
            const search = optionValue.slice(0, -1);
            return measurements
              .filter((m) => m.get('measurement').startsWith(search))
              .some((m) => form.getValue(`${measurementPath(m.get('measurement'))}.enabled`) !== false);
          }

          return form.getValue(`${measurementPath(optionValue)}.enabled`) !== false;
        });

      simpleField.init(values);
    });
  };

  getMeasurementMetricsByName = (measurementName) => {
    const measurementModel = this.measurements.find((m) => m.get('measurement') === measurementName);
    return measurementModel?.get('storage')?.Metrics || {};
  };

  setMeasurementMetricsEnabled = (
    measurementName,
    enabled,
    metrics = this.getMeasurementMetricsByName(measurementName)
  ) => {
    const { form } = this.props;

    Object.keys(metrics).forEach((metric) => {
      const metricField = form.getField(`${measurementPath(measurementName)}.metrics.${metric}.enabled`);
      if (metricField) {
        metricField.setValue(enabled);
      }
    });
  };

  setMeasurementAndMetricsEnabled = (measurementName, enabled, metrics) => {
    const { form } = this.props;
    const enabledField = form.getField(`${measurementPath(measurementName)}.enabled`);

    if (enabledField) {
      enabledField.setValue(enabled);
    }

    this.setMeasurementMetricsEnabled(measurementName, enabled, metrics);
  };

  ensureMeasurementGroupChecked = (group) => {
    runInAction(() => {
      group.measurements.forEach((m) => {
        const measurementName = m.get('measurement');
        const metrics = m.get('storage').Metrics || {};
        this.setMeasurementAndMetricsEnabled(measurementName, true, metrics);
      });
    });
  };

  resetMeasurementIntervalsToDefaults = (measurementModels) => {
    const { form } = this.props;
    const fastInterval = form.getValue('settings.intervals.fastMetrics');
    const slowInterval = form.getValue('settings.intervals.slowMetrics');

    runInAction(() => {
      measurementModels.forEach((measurement) => {
        const name = measurement.get('measurement');
        const defaultInterval = fastMeasurements.includes(name) ? fastInterval : slowInterval;
        form.getField(`${measurementPath(name)}.interval`)?.init(defaultInterval);
        form.getField(`${measurementPath(name)}.st_interval`)?.init(DEFAULT_GNMI_INTERVAL);
      });
    });
  };

  handleMeasurementGroupChange = (group, enabled) => {
    const { subscribedMeasurementGroups } = this;

    this.exitEverythingMode();

    if (!enabled && subscribedMeasurementGroups[group.key]) {
      this.setMeasurementGroupSubscription(group, false);
    }

    this.handleToggleMeasurementModels(group.measurements, enabled);
  };

  setMeasurementGroupSubscription = (group, subscribed) => {
    const { form } = this.props;
    this.exitEverythingMode();
    form.getField(MEASUREMENT_SUBSCRIPTION_FIELDS[group.key])?.setValue(subscribed);

    if (subscribed) {
      this.resetMeasurementIntervalsToDefaults(group.measurements);
      this.ensureMeasurementGroupChecked(group);
    }
  };

  handleMeasurementGroupSubscriptionChange = (group) => (subscribed) => {
    this.setMeasurementGroupSubscription(group, subscribed);
  };

  handleIntervalChange = () => {
    const { form } = this.props;
    const { measurements } = this;
    const fastInterval = form.getValue('settings.intervals.fastMetrics');
    const slowInterval = form.getValue('settings.intervals.slowMetrics');

    measurements.forEach((m) => {
      const name = m.get('measurement');
      const isFast = fastMeasurements.includes(name);
      const field = form.getField(`${measurementPath(name)}.interval`);

      if (field.getValue() === field.getInitialValue()) {
        field.init(isFast ? fastInterval : slowInterval);
      }
    });
  };

  handleInterfaceSelectorOpen = () => {
    this.setState({ interfaceSelectorOpen: true });
  };

  handleInterfaceSelectorClose = () => {
    this.setState({ interfaceSelectorOpen: false });
  };

  handleInterfaceGroupSave = () => {
    const { form } = this.props;
    form.setValue('_interfaceGroup', 'dirty');
    form.validate();
  };

  handleMeasurementGroupToggle = (groupKey) => {
    this.setState(({ expandedMeasurementGroups }) => ({
      expandedMeasurementGroups: {
        ...expandedMeasurementGroups,
        [groupKey]: !expandedMeasurementGroups[groupKey]
      }
    }));
  };

  handleParentMeasurementChange = (measurementName, metrics) => (field, value) => {
    this.exitEverythingMode();
    // batch updates to avoid n+1 issues
    runInAction(() => {
      this.setMeasurementMetricsEnabled(measurementName, value, metrics);
    });
  };

  handleToggleMeasurementModels = (measurementModels, enabled) => {
    // batch updates to avoid n+1 issues
    runInAction(() => {
      measurementModels.forEach((m) => {
        const measurementName = m.get('measurement');
        const metrics = m.get('storage').Metrics || {};
        this.setMeasurementAndMetricsEnabled(measurementName, enabled, metrics);
      });
    });

    this.syncSimpleMeasurementSelectionsFromEnabledFields();
  };

  handleToggleAllMeasurements = (enabled) => {
    const { form } = this.props;
    if (!enabled) {
      Object.values(MEASUREMENT_SUBSCRIPTION_FIELDS).forEach((fieldName) => {
        form.getField(fieldName)?.setValue(false);
      });
    }

    this.handleToggleMeasurementModels(this.measurements, enabled);
  };

  exitEverythingMode = () => {
    const { form } = this.props;
    const { allMeasurementsSubscribed } = this;

    if (!allMeasurementsSubscribed) {
      return;
    }

    form.getField(MEASUREMENT_SUBSCRIPTION_FIELDS.everything)?.setValue(false);
  };

  handleEverythingChange = (enabled) => {
    this.handleToggleAllMeasurements(enabled);
  };

  handleEverythingSubscriptionChange = (subscribed) => {
    const { form } = this.props;
    if (!subscribed) {
      form.getField(MEASUREMENT_SUBSCRIPTION_FIELDS.everything)?.setValue(false);
      return;
    }

    form.getField(MEASUREMENT_SUBSCRIPTION_FIELDS.everything)?.setValue(true);
    form.getField(MEASUREMENT_SUBSCRIPTION_FIELDS.essentials)?.setValue(false);
    form.getField(MEASUREMENT_SUBSCRIPTION_FIELDS.customSnmp)?.setValue(false);
    this.resetMeasurementIntervalsToDefaults(this.measurements);
    this.handleToggleMeasurementModels(this.measurements, true);
  };

  handleMeasurementCustomization = () => {
    this.exitEverythingMode();
  };

  getMeasurementGroupSelectionState = (measurementModels) => {
    const { form } = this.props;
    const enabledCount = measurementModels.filter((measurementModel) => {
      const measurementName = measurementModel.get('measurement');
      return form.getValue(`${measurementPath(measurementName)}.enabled`) !== false;
    }).length;

    return {
      checked: measurementModels.length > 0 && enabledCount === measurementModels.length,
      indeterminate: enabledCount > 0 && enabledCount < measurementModels.length
    };
  };

  setSnmpPollingFieldsToDefault = () => {
    const { form } = this.props;

    // Store user-entered values
    form.getField('uiProperties.polling.snmp.max-oids').setValue(form.getValue('settings.polling.snmp.max-oids'));
    form
      .getField('uiProperties.polling.snmp.max-repetitions')
      .setValue(form.getValue('settings.polling.snmp.max-repetitions'));
    form.getField('uiProperties.polling.snmp.timeout').setValue(form.getValue('settings.polling.snmp.timeout'));
    form.getField('uiProperties.polling.snmp.retries').setValue(form.getValue('settings.polling.snmp.retries'));
    form.getField('uiProperties.polling.snmp.workers').setValue(form.getValue('settings.polling.snmp.workers'));

    // Reset field values to default
    form.getMatchingFields(/^settings\.polling\.snmp.*/).forEach((field) => {
      field.setToDefault({ validate: false });
    });
  };

  restoreUserEnteredSnmpPollingValuesFromDefault = () => {
    const { form } = this.props;

    // Restore user-entered values
    form.getField('settings.polling.snmp.max-oids').setValue(form.getValue('uiProperties.polling.snmp.max-oids'));
    form
      .getField('settings.polling.snmp.max-repetitions')
      .setValue(form.getValue('uiProperties.polling.snmp.max-repetitions'));
    form.getField('settings.polling.snmp.timeout').setValue(form.getValue('uiProperties.polling.snmp.timeout'));
    form.getField('settings.polling.snmp.retries').setValue(form.getValue('uiProperties.polling.snmp.retries'));
    form.getField('settings.polling.snmp.workers').setValue(form.getValue('uiProperties.polling.snmp.workers'));
  };

  handleUseSnmpPollingDefaultsChange = ({ value: useNmsDefaults }) => {
    if (useNmsDefaults) {
      this.setSnmpPollingFieldsToDefault();
      return;
    }

    this.restoreUserEnteredSnmpPollingValuesFromDefault();
  };

  handleSubmit = (form, values) => {
    const { model } = form;
    const { allMeasurementsSubscribed, subscribedMeasurementGroups } = this;
    const settings = values.settings || {};

    serializeMeasurementSettings({
      settings,
      groups: groupMeasurements(this.measurements),
      subscriptions: subscribedMeasurementGroups,
      everythingSubscribed: allMeasurementsSubscribed
    });

    values.settings = settings;

    if (model.isNew && model.get('template_type')) {
      values.template_type = model.get('template_type');
    }
    return model.save(values);
  };

  formContent() {
    const { expandedMeasurementGroups, interfaceSelectorOpen } = this.state;
    const { allMeasurementsSubscribed, subscribedMeasurementGroups } = this;
    const { model, isDialog, form, $auth } = this.props;
    const { isReadOnly } = this;
    const measurementGroups = groupMeasurements(this.measurements);
    const allMeasurementsSelectionState = this.getMeasurementGroupSelectionState(this.measurements);
    const isMeasurementTreeReadOnly = isReadOnly || allMeasurementsSubscribed;
    const canManageCustomMeasurements =
      $auth.isAdministrator &&
      $auth.hasPermission('recon.customNmsReports.enabled', {
        overrideForSudo: false
      });

    let headingText = 'Edit';
    if (model.isNew) {
      headingText = 'Add';
    } else if (isReadOnly) {
      headingText = 'View';
    }
    return (
      <Box>
        {!isDialog && (
          <Flex gap={2} alignItems="center" justifyContent="space-between" mb={2}>
            <Heading level={4} mb={0}>
              <span>{headingText}</span> Monitoring Profile
            </Heading>
          </Flex>
        )}

        <Field
          name="name"
          rules={`required|${this.uniqueNameRule}`}
          component={InputGroup}
          large
          disabled={isReadOnly}
        />
        <Field name="description" component={InputGroup} large disabled={isReadOnly} />

        {!this.isFlowTemplate && (
          <>
            <HR />
            <Heading level={5} mb="4px">
              Interface selection
            </Heading>
            <Text muted>Choose which interfaces will be monitored</Text>
            <Box mt={2}>
              <InterfaceGroupSelector
                isOpen={interfaceSelectorOpen}
                interfaceGroup={model.interfaceGroup}
                moduleName={model.moduleName}
                onInterfaceGroupSave={this.handleInterfaceGroupSave}
                onInterfaceGroupDialogClose={this.handleInterfaceSelectorClose}
                staticInterfacesTitle="Static Group"
                showAdminStatus
              />
              <Button
                icon="edit"
                text="Edit Selections"
                onClick={this.handleInterfaceSelectorOpen}
                disabled={isReadOnly}
              />
            </Box>
          </>
        )}

        {this.isFlowTemplate && (
          <>
            <HR />
            <Heading level={5} mb="4px">
              SNMP Polling Intervals
            </Heading>
            <Text muted>
              Basic polling is highly recommended. If basic polling is selected the Universal Agent NMS client will poll
              interface counter readings every 5 minutes and poll descriptions every 3 hours. This polling is required
              for the Kentik Map to properly populate with health statistics and for Connectivity costs to accurately
              reflect common ISP billing intervals. If Minimum polling is selected, interface polling is disabled and
              descriptions are polled every 6 hours.
            </Text>
            <Flex mt={2}>
              <Field name="settings.flow-mode" large disabled={isReadOnly}>
                <RadioGroup containerProps={{ flexDirection: 'column', gap: '8px' }} />
              </Field>
            </Flex>
          </>
        )}

        {/* Flow templates have limited options, do not allow them to edit polling intervals beyond setting the flow-mode */}
        {!this.isFlowTemplate && (
          <>
            <HR />
            <Heading level={5} mb="4px">
              Polling intervals
            </Heading>
            <Text muted>Determine how often data is collected for specific metrics</Text>
            <Flex gap={2} mt={2} flex="1" minWidth="200px" maxWidth={600}>
              <Box flex="1">
                <Field
                  name="settings.intervals.fastMetrics"
                  onChange={this.handleIntervalChange}
                  component={Duration}
                  large
                  disabled={isReadOnly}
                />
              </Box>
              <Box flex="1">
                <Field
                  name="settings.intervals.slowMetrics"
                  onChange={this.handleIntervalChange}
                  component={Duration}
                  large
                  disabled={isReadOnly}
                />
              </Box>
              <Box flex="1">
                <Field name="settings.intervals.metadata" component={Duration} large disabled={isReadOnly} />
              </Box>
            </Flex>
          </>
        )}

        <PollingConfigSection
          form={form}
          isReadOnly={isReadOnly}
          onUseSnmpPollingDefaultsChange={this.handleUseSnmpPollingDefaultsChange}
        />

        <HR />

        {/* Do not allow flow templates to edit measurement selection. */}
        {!this.isFlowTemplate && (
          <>
            <Flex alignItems="flex-start" gap={3} mt={2} flexDirection={['column', 'column', 'row']} minWidth={0}>
              <Box flex="1" width="100%" minWidth={0}>
                <Box width="70%" mt={2}>
                  <Heading level={5} mb="4px">
                    Select measurements
                  </Heading>
                  <Text as="div" muted my={1}>
                    Choose which measurements to collect and at what intervals for devices using this Monitoring
                    Profile. By default, your profile will use Recommended measurements, which are managed by Kentik and
                    provide the most useful data for your devices. You can subscribe to future changes to the
                    measurement sets by enabling <b>Automatic Updates</b>.
                  </Text>
                  <Text as="div" muted small my={2}>
                    Note: gNMI collection will be preferred over SNMP where available.
                  </Text>
                </Box>
                <Box mt={2}>
                  <Flex gap={1} justifyContent="flex-start" mb={2}>
                    <Flex gap={1}>
                      <Button
                        text="Select All"
                        outlined
                        small
                        disabled={isMeasurementTreeReadOnly}
                        onClick={() => this.handleToggleAllMeasurements(true)}
                      />
                      <Button
                        text="Select None"
                        outlined
                        small
                        disabled={isMeasurementTreeReadOnly}
                        onClick={() => this.handleToggleAllMeasurements(false)}
                      />
                    </Flex>
                  </Flex>
                  <MeasurementTable condensed width="100%" mt={2}>
                    <colgroup>
                      <col style={{ width: 30 }} />
                      <col />
                      <col style={{ width: 190 }} />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 160 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th colSpan={2}>Measurement</th>
                        <th>Automatic Updates</th>
                        <th>SNMP Interval</th>
                        <th>gNMI Interval</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th colSpan={2}>
                          <CoreCheckbox
                            checked={allMeasurementsSelectionState.checked}
                            indeterminate={allMeasurementsSelectionState.indeterminate}
                            ariaLabel={`Everything, ${this.measurements.length} measurements`}
                            labelElement={
                              <Text fontSize="14px" fontWeight="bold">
                                Everything{' '}
                                <Text as="span" fontWeight="normal" muted>
                                  ({this.measurements.length})
                                </Text>
                              </Text>
                            }
                            disabled={isReadOnly || this.measurements.length === 0}
                            onChange={this.handleEverythingChange}
                          />
                        </th>
                        <td>
                          <Box
                            width="max-content"
                            title="Subscribe to future changes to all measurement sets by enabling Automatic Updates"
                          >
                            <Switch
                              checked={allMeasurementsSubscribed}
                              label="Keep updated"
                              ariaLabel="Subscribe to future changes to all measurement sets by enabling Automatic Updates"
                              disabled={isReadOnly}
                              onChange={this.handleEverythingSubscriptionChange}
                            />
                          </Box>
                        </td>
                        <td colSpan={2} />
                      </tr>
                      {measurementGroups.map((group) => {
                        const selectionState = this.getMeasurementGroupSelectionState(group.measurements);
                        const expanded = expandedMeasurementGroups[group.key];
                        const subscribed = !!subscribedMeasurementGroups[group.key];
                        const canSubscribe = !!group.tag;

                        return (
                          <React.Fragment key={group.key}>
                            <tr>
                              <td>
                                <Button
                                  onClick={() => this.handleMeasurementGroupToggle(group.key)}
                                  icon={expanded ? 'caret-down' : 'caret-right'}
                                  minimal
                                  title={expanded ? `Collapse ${group.label}` : `Expand ${group.label}`}
                                />
                              </td>
                              <th>
                                <CoreCheckbox
                                  checked={selectionState.checked}
                                  indeterminate={selectionState.indeterminate}
                                  ariaLabel={`${group.label}, ${group.measurements.length} measurements`}
                                  labelElement={
                                    <Text fontSize="14px" fontWeight="bold">
                                      {group.label}{' '}
                                      <Text as="span" fontWeight="normal" muted>
                                        ({group.measurements.length})
                                      </Text>
                                    </Text>
                                  }
                                  disabled={isMeasurementTreeReadOnly || group.measurements.length === 0}
                                  onChange={(checked) => this.handleMeasurementGroupChange(group, checked)}
                                />
                              </th>
                              <td>
                                {canSubscribe && (
                                  <Box width="max-content" title={group.subscriptionAriaLabel}>
                                    <Switch
                                      checked={subscribed}
                                      label={group.subscriptionLabel}
                                      ariaLabel={group.subscriptionAriaLabel}
                                      disabled={isMeasurementTreeReadOnly}
                                      onChange={this.handleMeasurementGroupSubscriptionChange(group)}
                                    />
                                  </Box>
                                )}
                              </td>
                              <td colSpan={2} />
                            </tr>
                            {expanded &&
                              group.measurements.map((measurement) =>
                                this.measurementRowElement(measurement, subscribed)
                              )}
                            {expanded && group.key === 'customSnmp' && group.measurements.length === 0 && (
                              <tr>
                                <td />
                                <td colSpan={4}>
                                  <Box pl="20px" py={1}>
                                    <Text muted>
                                      No custom measurements available.
                                      {canManageCustomMeasurements && (
                                        <>
                                          {' '}
                                          <Link to="/v4/settings/custom-nms-reports" blank>
                                            Create a Custom NMS Report
                                          </Link>{' '}
                                          in Settings.
                                        </>
                                      )}
                                    </Text>
                                  </Box>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </MeasurementTable>
                </Box>
              </Box>
            </Flex>
          </>
        )}
      </Box>
    );
  }

  measurementRowElement = (measurementModel, groupSubscribed) => {
    const { isReadOnly } = this;
    const name = measurementModel.get('measurement');
    const metrics = measurementModel.get('storage').Metrics || {};
    const isMeasurementTreeReadOnly = isReadOnly || this.allMeasurementsSubscribed;
    const isMeasurementSelectionReadOnly = isMeasurementTreeReadOnly || groupSubscribed;
    return (
      <tr key={measurementModel.id}>
        <td />
        <td>
          <Box pl="20px">
            <Field
              name={`${measurementPath(name)}.enabled`}
              showLabel={false}
              mb={0}
              large
              onChange={this.handleParentMeasurementChange(name, metrics)}
              disabled={isMeasurementSelectionReadOnly}
            >
              <Checkbox
                label={
                  <Box maxWidth="520px">
                    <Text as="div" ellipsis title={name}>
                      {name}
                    </Text>
                  </Box>
                }
              />
            </Field>
          </Box>
        </td>
        <td />
        <td>
          <Field
            name={`${measurementPath(name)}.interval`}
            component={Duration}
            onChange={this.handleMeasurementCustomization}
            mb={0}
            disabled={isMeasurementSelectionReadOnly}
          />
        </td>
        <td>
          <Field
            name={`${measurementPath(name)}.st_interval`}
            component={Duration}
            onChange={this.handleMeasurementCustomization}
            mb={0}
            disabled={isMeasurementSelectionReadOnly}
          />
        </td>
      </tr>
    );
  };

  render() {
    const { model, form, isDialog, isOpen, onClose } = this.props;
    const { isReadOnly } = this;

    if (isDialog) {
      const { isFlowTemplate } = this;
      return (
        <FormDialog
          form={form}
          model={model}
          isOpen={isOpen}
          canClickOutsideToClose={false}
          entityName={`${isFlowTemplate ? 'Flow Enrichment' : 'NMS'} Monitoring Profile`}
          onClose={onClose}
          minWidth={1200}
          formActionsProps={{
            onSubmit: isReadOnly ? undefined : this.handleSubmit,
            submitText: isReadOnly ? undefined : 'Save',
            cancelText: isReadOnly ? 'Close' : 'Cancel',
            otherActions: null
          }}
        >
          {isReadOnly && (
            <Box mb={2} p={2} backgroundColor="rgba(92, 112, 128, 0.1)" borderRadius={3}>
              <Text muted>
                <strong>System Profile:</strong> This is a read-only preset profile that cannot be modified.
              </Text>
            </Box>
          )}
          {this.formContent()}
        </FormDialog>
      );
    }

    return this.formContent();
  }
}
